import {noop} from '@benchling/js-utils';
import gql from 'graphql-tag';
import {v4 as uuidv4} from 'uuid';

import {assert} from 'Asserts';
import {doQuerySafe, readFragmentFromCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import {isInaccessible as isInaccessibleGQL} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import APIUtil from 'util/APIUtil';
import {BasicEvent} from 'util/Types';

import {InaccessibleInfoGraphQL} from '../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {DatasetChipGraphQL} from '../../../graphql-fragments/data-frames/__generated/DatasetChipGraphQL';
import {FileChipGraphQL} from '../../../graphql-fragments/files/__generated/FileChipGraphQL';
import {StorableChipSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableChipSummaryGraphQL';
import {BulkAssemblyChipSummaryGraphQL} from '../../../graphql-fragments/molbio/bulk-assembly/__generated/BulkAssemblyChipSummaryGraphQL';
import {EntrySummaryGraphQL} from '../../../graphql-fragments/notebook/__generated/EntrySummaryGraphQL';
import {DocumentChipGraphQL} from '../../../graphql-fragments/notebook/documents/__generated/DocumentChipGraphQL';
import {PartyChipGraphQL} from '../../../graphql-fragments/parties/__generated/PartyChipGraphQL';
import {TestDefinitionChipGraphQL} from '../../../graphql-fragments/procedures/__generated/TestDefinitionChipGraphQL';
import {BioentityChipSummaryGraphQL} from '../../../graphql-fragments/registry/registry-items/__generated/BioentityChipSummaryGraphQL';
import {RequestGraphQL} from '../../../graphql-fragments/requests/__generated/RequestGraphQL';
import {TestOrderChipGraphQL} from '../../../graphql-fragments/sample_tests/__generated/TestOrderChipGraphQL';
import {SampleDefinitionChipGraphQL} from '../../../graphql-fragments/studies/__generated/SampleDefinitionChipGraphQL';
import {StageEntrySummaryGraphQL} from '../../../graphql-fragments/workflows/__generated/StageEntrySummaryGraphQL';
import {WorklistSummaryGraphQL} from '../../../graphql-fragments/worklists/__generated/WorklistSummaryGraphQL';
import {
  BulkChipItemPrefetchQuery,
  BulkChipItemPrefetchQueryVariables,
} from './__generated/BulkChipItemPrefetchQuery';

type Analysis = ExtractByTypename<BulkChipItemPrefetchQuery['analyses'][number], 'AnalysisNode'>;

type AnalysisTemplate = ExtractByTypename<
  BulkChipItemPrefetchQuery['analysisTemplates'][number],
  'AnalysisTemplateNode'
>;

type AutomationOutputFile = ExtractByTypename<
  BulkChipItemPrefetchQuery['automationOutputFiles'][number],
  'AutomationOutputFileNode'
>;

type Connection = ExtractByTypename<BulkChipItemPrefetchQuery['connections'][number], 'ConnectionNode'>;

type Procedure = ExtractByTypename<BulkChipItemPrefetchQuery['procedures'][number], 'ProcedureNode'>;

type ProcedureMethodExecutionInstance = ExtractByTypename<
  BulkChipItemPrefetchQuery['procedureMethodExecutionInstances'][number],
  'ProcedureMethodExecutionInstanceNode'
>;

type Process = ExtractByTypename<BulkChipItemPrefetchQuery['processes'][number], 'ProcessNode'>;

type ProcessOutput = ExtractByTypename<
  BulkChipItemPrefetchQuery['processOutputs'][number],
  'ProcessOutputNode'
>;

type ProcessTask = ExtractByTypename<BulkChipItemPrefetchQuery['processTasks'][number], 'ProcessTaskNode'>;

type RequestV2Submission = ExtractByTypename<
  BulkChipItemPrefetchQuery['requestV2Submissions'][number],
  'RequestV2SubmissionNode'
>;

type Run = ExtractByTypename<BulkChipItemPrefetchQuery['runs'][number], 'RunNode'>;

type StorableSchema = ExtractByTypename<
  BulkChipItemPrefetchQuery['storableSchemas'][number],
  'BoxSchemaNode'
>;

type Study = ExtractByTypename<BulkChipItemPrefetchQuery['studies'][number], 'StudyNode'>;

type TagSchema = ExtractByTypename<BulkChipItemPrefetchQuery['tagSchemaChips'][number], 'TagSchemaNode'>;

type Template = ExtractByTypename<BulkChipItemPrefetchQuery['templatesByIds'][number], 'TemplateNode'>;

type Worksheet = ExtractByTypename<BulkChipItemPrefetchQuery['worksheets'][number], 'WorksheetNode'>;

export enum SmartChipType {
  ANALYSIS = 'ANALYSIS',
  ANALYSIS_TEMPLATE = 'ANALYSIS_TEMPLATE',
  AUTOMATION_OUTPUT_FILE = 'AUTOMATION_OUTPUT_FILE',
  BIOENTITY = 'BIOENTITY',
  BULK_ASSEMBLY = 'BULK_ASSEMBLY',
  CONNECTION = 'CONNECTION',
  DATASET = 'DATASET',
  DOCUMENT = 'DOCUMENT',
  ENTRY = 'ENTRY',
  FILE = 'FILE',
  PARTY = 'PARTY',
  PROCEDURE = 'PROCEDURE',
  PROCEDURE_METHOD_EXECUTION_INSTANCE = 'PROCEDURE_METHOD_EXECUTION_INSTANCE',
  PROCESS = 'PROCESS',
  PROCESS_OUTPUT = 'PROCESS_OUTPUT',
  PROCESS_TASK = 'PROCESS_TASK',
  REQUEST = 'REQUEST',
  REQUEST_V2_SUBMISSION = 'REQUEST_V2_SUBMISSION',
  RUN = 'RUN',
  SAMPLE_DEFINITION = 'SAMPLE_DEFINITION',
  STORABLE_SCHEMA = 'STORABLE_SCHEMA',
  STAGE_ENTRY = 'STAGE_ENTRY',
  STORABLE = 'STORABLE',
  STUDY = 'STUDY',
  TAG_SCHEMA = 'TAG_SCHEMA',
  TEMPLATE = 'TEMPLATE',
  TEST_DEFINITION = 'TEST_DEFINITION',
  TEST_ORDER = 'TEST_ORDER',
  WORKLIST = 'WORKLIST',
  WORKSHEET = 'WORKSHEET',
}

interface ChipItemMapping {
  [SmartChipType.ANALYSIS]: Analysis;
  [SmartChipType.ANALYSIS_TEMPLATE]: AnalysisTemplate;
  [SmartChipType.AUTOMATION_OUTPUT_FILE]: AutomationOutputFile;
  [SmartChipType.BIOENTITY]: BioentityChipSummaryGraphQL;
  [SmartChipType.BULK_ASSEMBLY]: BulkAssemblyChipSummaryGraphQL;
  [SmartChipType.CONNECTION]: Connection;
  [SmartChipType.DATASET]: DatasetChipGraphQL;
  [SmartChipType.DOCUMENT]: DocumentChipGraphQL;
  [SmartChipType.ENTRY]: EntrySummaryGraphQL;
  [SmartChipType.FILE]: FileChipGraphQL;
  [SmartChipType.PARTY]: PartyChipGraphQL;
  [SmartChipType.PROCEDURE]: Procedure;
  [SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE]: ProcedureMethodExecutionInstance & {name: string};
  [SmartChipType.PROCESS]: Process;
  [SmartChipType.PROCESS_OUTPUT]: ProcessOutput;
  [SmartChipType.PROCESS_TASK]: ProcessTask;
  [SmartChipType.REQUEST]: RequestGraphQL;
  [SmartChipType.REQUEST_V2_SUBMISSION]: RequestV2Submission;
  [SmartChipType.RUN]: Run & {name: string};
  [SmartChipType.SAMPLE_DEFINITION]: SampleDefinitionChipGraphQL;
  [SmartChipType.STAGE_ENTRY]: StageEntrySummaryGraphQL;
  [SmartChipType.STORABLE]: StorableChipSummaryGraphQL;
  [SmartChipType.STORABLE_SCHEMA]: StorableSchema;
  [SmartChipType.STUDY]: Study;
  [SmartChipType.TAG_SCHEMA]: TagSchema;
  [SmartChipType.TEMPLATE]: Template;
  [SmartChipType.TEST_DEFINITION]: TestDefinitionChipGraphQL;
  [SmartChipType.TEST_ORDER]: TestOrderChipGraphQL;
  [SmartChipType.WORKLIST]: WorklistSummaryGraphQL;
  [SmartChipType.WORKSHEET]: Worksheet;
}

// This line will raise a type error if you add a SmartChipType but forget to add to ChipItemMapping
export type ChipItem<T extends SmartChipType> = ChipItemMapping[T];

export type ChipItemOrInaccessible<T extends SmartChipType> = ChipItem<T> | InaccessibleInfoGraphQL;

interface SmartChipQueryConstants<T extends SmartChipType> {
  fragmentWrapper: FragmentWrapper<ChipItemOrInaccessible<T>>;
  queryKeys: {
    dataKey: keyof BulkChipItemPrefetchQuery;
    variablesKey: keyof BulkChipItemPrefetchQueryVariables;
  };
}

export const SMART_CHIP_QUERY_CONSTANTS: {
  [type in SmartChipType]: SmartChipQueryConstants<type>;
} = {
  [SmartChipType.ANALYSIS]: {
    fragmentWrapper: getFragmentWrapper('AnalysisChipOrInaccessible'),
    queryKeys: {dataKey: 'analyses', variablesKey: 'analysisIds'},
  },
  [SmartChipType.ANALYSIS_TEMPLATE]: {
    fragmentWrapper: getFragmentWrapper('AnalysisTemplateChipOrInaccessible'),
    queryKeys: {dataKey: 'analysisTemplates', variablesKey: 'analysisTemplateIds'},
  },
  [SmartChipType.AUTOMATION_OUTPUT_FILE]: {
    fragmentWrapper: getFragmentWrapper('AutomationOutputFileChipOrInaccessible'),
    queryKeys: {dataKey: 'automationOutputFiles', variablesKey: 'automationOutputFileIds'},
  },
  [SmartChipType.BIOENTITY]: {
    fragmentWrapper: getFragmentWrapper('BioentityChipOrInaccessible'),
    queryKeys: {dataKey: 'bioentities', variablesKey: 'bioentityIds'},
  },
  [SmartChipType.BULK_ASSEMBLY]: {
    fragmentWrapper: getFragmentWrapper('BulkAssemblyChipOrInaccessible'),
    queryKeys: {dataKey: 'bulkAssemblies', variablesKey: 'bulkAssemblyIds'},
  },
  [SmartChipType.CONNECTION]: {
    fragmentWrapper: getFragmentWrapper('ConnectionChipOrInaccessible'),
    queryKeys: {dataKey: 'connections', variablesKey: 'connectionIds'},
  },
  [SmartChipType.DATASET]: {
    fragmentWrapper: getFragmentWrapper('DatasetChipOrInaccessible'),
    queryKeys: {dataKey: 'datasets', variablesKey: 'datasetIds'},
  },
  [SmartChipType.DOCUMENT]: {
    fragmentWrapper: getFragmentWrapper('DocumentChipOrInaccessible'),
    queryKeys: {dataKey: 'documents', variablesKey: 'documentIds'},
  },
  [SmartChipType.ENTRY]: {
    fragmentWrapper: getFragmentWrapper('EntryChipOrInaccessible'),
    queryKeys: {dataKey: 'entries', variablesKey: 'entryIds'},
  },
  [SmartChipType.FILE]: {
    fragmentWrapper: getFragmentWrapper('FileChipOrInaccessible'),
    queryKeys: {dataKey: 'files', variablesKey: 'fileIds'},
  },
  [SmartChipType.PARTY]: {
    fragmentWrapper: getFragmentWrapper('PartyChipOrInaccessible'),
    queryKeys: {dataKey: 'parties', variablesKey: 'partyIds'},
  },
  [SmartChipType.PROCEDURE]: {
    fragmentWrapper: getFragmentWrapper('ProcedureChipOrInaccessible'),
    queryKeys: {dataKey: 'procedures', variablesKey: 'procedureIds'},
  },
  [SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE]: {
    fragmentWrapper: getFragmentWrapper('ProcedureMethodExecutionInstanceChipOrInaccessible'),
    queryKeys: {
      dataKey: 'procedureMethodExecutionInstances',
      variablesKey: 'procedureMethodExecutionInstanceIds',
    },
  },
  [SmartChipType.PROCESS]: {
    fragmentWrapper: getFragmentWrapper('ProcessChipOrInaccessible'),
    queryKeys: {dataKey: 'processes', variablesKey: 'processIds'},
  },
  [SmartChipType.PROCESS_OUTPUT]: {
    fragmentWrapper: getFragmentWrapper('ProcessOutputChipOrInaccessible'),
    queryKeys: {dataKey: 'processOutputs', variablesKey: 'processOutputIds'},
  },
  [SmartChipType.PROCESS_TASK]: {
    fragmentWrapper: getFragmentWrapper('ProcessTaskChipOrInaccessible'),
    queryKeys: {dataKey: 'processTasks', variablesKey: 'processTaskIds'},
  },
  [SmartChipType.REQUEST]: {
    fragmentWrapper: getFragmentWrapper('RequestChipOrInaccessible'),
    queryKeys: {dataKey: 'requests', variablesKey: 'requestIds'},
  },
  [SmartChipType.REQUEST_V2_SUBMISSION]: {
    fragmentWrapper: getFragmentWrapper('RequestV2SubmissionChipOrInaccessible'),
    queryKeys: {dataKey: 'requestV2Submissions', variablesKey: 'requestV2SubmissionIds'},
  },
  [SmartChipType.RUN]: {
    fragmentWrapper: getFragmentWrapper('RunChipOrInaccessible'),
    queryKeys: {dataKey: 'runs', variablesKey: 'runIds'},
  },
  [SmartChipType.SAMPLE_DEFINITION]: {
    fragmentWrapper: getFragmentWrapper('SampleDefinitionChipOrInaccessible'),
    queryKeys: {dataKey: 'sampleDefinitions', variablesKey: 'sampleDefinitionIds'},
  },
  [SmartChipType.STAGE_ENTRY]: {
    fragmentWrapper: getFragmentWrapper('StageEntryChipOrInaccessible'),
    queryKeys: {dataKey: 'stageEntries', variablesKey: 'stageEntryIds'},
  },
  [SmartChipType.STORABLE]: {
    fragmentWrapper: getFragmentWrapper('StorableChipOrInaccessible'),
    queryKeys: {dataKey: 'storables', variablesKey: 'storableIds'},
  },
  [SmartChipType.STORABLE_SCHEMA]: {
    fragmentWrapper: getFragmentWrapper('StorableSchemaChipOrInaccessible'),
    queryKeys: {dataKey: 'storableSchemas', variablesKey: 'storableSchemaIds'},
  },
  [SmartChipType.STUDY]: {
    fragmentWrapper: getFragmentWrapper('StudyChipOrInaccessible'),
    queryKeys: {dataKey: 'studies', variablesKey: 'studyIds'},
  },
  [SmartChipType.TAG_SCHEMA]: {
    fragmentWrapper: getFragmentWrapper('TagSchemaChipOrInaccessible'),
    queryKeys: {dataKey: 'tagSchemaChips', variablesKey: 'tagSchemaIds'},
  },
  [SmartChipType.TEMPLATE]: {
    fragmentWrapper: getFragmentWrapper('TemplateChipOrInaccessible'),
    queryKeys: {dataKey: 'templatesByIds', variablesKey: 'templateIds'},
  },
  [SmartChipType.TEST_DEFINITION]: {
    fragmentWrapper: getFragmentWrapper('TestDefinitionChipOrInaccessible'),
    queryKeys: {dataKey: 'testDefinitions', variablesKey: 'testDefinitionIds'},
  },
  [SmartChipType.TEST_ORDER]: {
    fragmentWrapper: getFragmentWrapper('TestOrderChipOrInaccessible'),
    queryKeys: {dataKey: 'testOrders', variablesKey: 'testOrderIds'},
  },
  [SmartChipType.WORKLIST]: {
    fragmentWrapper: getFragmentWrapper('WorklistChipOrInaccessible'),
    queryKeys: {dataKey: 'worklists', variablesKey: 'worklistIds'},
  },
  [SmartChipType.WORKSHEET]: {
    fragmentWrapper: getFragmentWrapper('WorksheetChipOrInaccessible'),
    queryKeys: {dataKey: 'worksheets', variablesKey: 'worksheetIds'},
  },
};

export const BULK_CHIP_ITEM_PREFETCH_QUERY = gql`
  query BulkChipItemPrefetchQuery(
    $analysisIds: [StrictString!]!
    $analysisTemplateIds: [StrictString!]!
    $automationOutputFileIds: [StrictString!]!
    $bioentityIds: [StrictString!]!
    $bulkAssemblyIds: [StrictString!]!
    $connectionIds: [StrictString!]!
    $datasetIds: [StrictString!]!
    $documentIds: [StrictString!]!
    $entryIds: [StrictString!]!
    $fileIds: [StrictString!]!
    $partyIds: [StrictString!]!
    $procedureIds: [StrictString!]!
    $procedureMethodExecutionInstanceIds: [StrictString!]!
    $processIds: [StrictString!]!
    $processOutputIds: [StrictString!]!
    $processTaskIds: [StrictString!]!
    $requestIds: [StrictString!]!
    $requestV2SubmissionIds: [StrictString!]!
    $runIds: [StrictString!]!
    $sampleDefinitionIds: [StrictString!]!
    $stageEntryIds: [StrictString!]!
    $storableIds: [StrictString!]!
    $storableSchemaIds: [StrictString!]!
    $studyIds: [StrictString!]!
    $tagSchemaIds: [StrictString!]!
    $templateIds: [StrictString!]!
    $testDefinitionIds: [StrictString!]!
    $testOrderIds: [StrictString!]!
    $worklistIds: [StrictString!]!
    $worksheetIds: [StrictString!]!
  ) @skipAudit @engComponent(name: UI_LIBRARY) {
    analyses(ids: $analysisIds) {
      ...AnalysisChipOrInaccessibleGraphQL
    }
    analysisTemplates(ids: $analysisTemplateIds) {
      ...AnalysisTemplateChipOrInaccessibleGraphQL
    }
    automationOutputFiles(ids: $automationOutputFileIds) {
      ...AutomationOutputFileChipOrInaccessibleGraphQL
    }
    bioentities(ids: $bioentityIds) {
      ...BioentityChipOrInaccessibleGraphQL
    }
    bulkAssemblies(ids: $bulkAssemblyIds) {
      ...BulkAssemblyChipOrInaccessibleGraphQL
    }
    connections(ids: $connectionIds) {
      ...ConnectionChipOrInaccessibleGraphQL
    }
    datasets(ids: $datasetIds) {
      ...DatasetChipOrInaccessibleGraphQL
    }
    documents(ids: $documentIds) {
      ...DocumentChipOrInaccessibleGraphQL
    }
    entries(ids: $entryIds) {
      ...EntryChipOrInaccessibleGraphQL
    }
    files(ids: $fileIds) {
      ...FileChipOrInaccessibleGraphQL
    }
    parties(ids: $partyIds) {
      ...PartyChipOrInaccessibleGraphQL
    }
    procedures(ids: $procedureIds) {
      ...ProcedureChipOrInaccessibleGraphQL
    }
    procedureMethodExecutionInstances(ids: $procedureMethodExecutionInstanceIds) {
      ...ProcedureMethodExecutionInstanceChipOrInaccessibleGraphQL
    }
    processes(ids: $processIds) {
      ...ProcessChipOrInaccessibleGraphQL
    }
    processOutputs(ids: $processOutputIds) {
      ...ProcessOutputChipOrInaccessibleGraphQL
    }
    processTasks(ids: $processTaskIds) {
      ...ProcessTaskChipOrInaccessibleGraphQL
    }
    requests(ids: $requestIds) {
      ...RequestChipOrInaccessibleGraphQL
    }
    requestV2Submissions(ids: $requestV2SubmissionIds) {
      ...RequestV2SubmissionChipOrInaccessibleGraphQL
    }
    runs(ids: $runIds) {
      ...RunChipOrInaccessibleGraphQL
    }
    sampleDefinitions(ids: $sampleDefinitionIds) {
      ...SampleDefinitionChipOrInaccessibleGraphQL
    }
    storableSchemas(ids: $storableSchemaIds) {
      ...StorableSchemaChipOrInaccessibleGraphQL
    }
    stageEntries(ids: $stageEntryIds) {
      ...StageEntryChipOrInaccessibleGraphQL
    }
    storables(ids: $storableIds) {
      ...StorableChipOrInaccessibleGraphQL
    }
    studies(ids: $studyIds) {
      ...StudyChipOrInaccessibleGraphQL
    }
    tagSchemaChips(ids: $tagSchemaIds) {
      ...TagSchemaChipOrInaccessibleGraphQL
    }
    templatesByIds(ids: $templateIds) {
      ...TemplateChipOrInaccessibleGraphQL
    }
    testDefinitions(ids: $testDefinitionIds) {
      ...TestDefinitionChipOrInaccessibleGraphQL
    }
    testOrders(ids: $testOrderIds) {
      ...TestOrderChipOrInaccessibleGraphQL
    }
    worklists(ids: $worklistIds) {
      ...WorklistChipOrInaccessibleGraphQL
    }
    worksheets(ids: $worksheetIds) {
      ...WorksheetChipOrInaccessibleGraphQL
    }
  }
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.ANALYSIS].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.ANALYSIS_TEMPLATE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.AUTOMATION_OUTPUT_FILE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.BIOENTITY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.BULK_ASSEMBLY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.CONNECTION].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.DATASET].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.DOCUMENT].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.ENTRY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.FILE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PARTY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PROCEDURE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PROCESS].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PROCESS_OUTPUT].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.PROCESS_TASK].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.REQUEST].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.REQUEST_V2_SUBMISSION].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.RUN].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.SAMPLE_DEFINITION].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.STORABLE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.STORABLE_SCHEMA].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.STAGE_ENTRY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.STUDY].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.TAG_SCHEMA].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.TEMPLATE].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.TEST_DEFINITION].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.TEST_ORDER].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.WORKLIST].fragmentWrapper.fragment}
  ${SMART_CHIP_QUERY_CONSTANTS[SmartChipType.WORKSHEET].fragmentWrapper.fragment}
`;

export function getSmartChipTypeFromId(id: string): SmartChipType {
  if (APIUtil.isAnalysis(id)) {
    return SmartChipType.ANALYSIS;
  } else if (APIUtil.isAnalysisTemplate?.(id)) {
    return SmartChipType.ANALYSIS_TEMPLATE;
  } else if (APIUtil.isAutomationOutputFile(id)) {
    return SmartChipType.AUTOMATION_OUTPUT_FILE;
  } else if (APIUtil.isBiologicalEntity(id) || APIUtil.isMolecule(id)) {
    return SmartChipType.BIOENTITY;
  } else if (APIUtil.isBulkAssembly(id)) {
    return SmartChipType.BULK_ASSEMBLY;
  } else if (APIUtil.isConnection(id)) {
    return SmartChipType.CONNECTION;
  } else if (APIUtil.isDataset(id)) {
    return SmartChipType.DATASET;
  } else if (APIUtil.isDocument(id)) {
    return SmartChipType.DOCUMENT;
  } else if (APIUtil.isEntry(id)) {
    return SmartChipType.ENTRY;
  } else if (APIUtil.isFile(id)) {
    return SmartChipType.FILE;
  } else if (APIUtil.isParty(id)) {
    return SmartChipType.PARTY;
  } else if (APIUtil.isProcedure(id)) {
    return SmartChipType.PROCEDURE;
  } else if (APIUtil.isProcedureMethodExecutionInstance(id)) {
    return SmartChipType.PROCEDURE_METHOD_EXECUTION_INSTANCE;
  } else if (APIUtil.isProcess(id)) {
    return SmartChipType.PROCESS;
  } else if (APIUtil.isProcessOutput(id)) {
    return SmartChipType.PROCESS_OUTPUT;
  } else if (APIUtil.isProcessTask(id)) {
    return SmartChipType.PROCESS_TASK;
  } else if (APIUtil.isRequest(id)) {
    return SmartChipType.REQUEST;
  } else if (APIUtil.isRequestV2Submission(id)) {
    return SmartChipType.REQUEST_V2_SUBMISSION;
  } else if (APIUtil.isRun(id)) {
    return SmartChipType.RUN;
  } else if (APIUtil.isSampleDefinition(id)) {
    return SmartChipType.SAMPLE_DEFINITION;
  } else if (APIUtil.isStorable(id)) {
    return SmartChipType.STORABLE;
  } else if (APIUtil.isStorableSchema(id)) {
    return SmartChipType.STORABLE_SCHEMA;
  } else if (APIUtil.isStageEntry(id)) {
    return SmartChipType.STAGE_ENTRY;
  } else if (APIUtil.isStudy(id)) {
    return SmartChipType.STUDY;
  } else if (APIUtil.isTagSchema(id)) {
    return SmartChipType.TAG_SCHEMA;
  } else if (APIUtil.isEntryTemplate(id)) {
    return SmartChipType.TEMPLATE;
  } else if (APIUtil.isTestDefinition(id)) {
    return SmartChipType.TEST_DEFINITION;
  } else if (APIUtil.isTestOrder(id)) {
    return SmartChipType.TEST_ORDER;
  } else if (APIUtil.isWorklist(id)) {
    return SmartChipType.WORKLIST;
  } else if (APIUtil.isWorksheet(id)) {
    return SmartChipType.WORKSHEET;
  }

  assert(false, `Unexpected chip type with id='${id}'`);
}

export function getFragmentByChipId<T extends SmartChipType>(
  id: string
): FragmentWrapper<ChipItemOrInaccessible<T>> {
  return SMART_CHIP_QUERY_CONSTANTS[getSmartChipTypeFromId(id)].fragmentWrapper;
}

export function getPrefetchQueryVariables(ids: Array<string>): BulkChipItemPrefetchQueryVariables {
  const emptyVariables: Partial<BulkChipItemPrefetchQueryVariables> = {};
  Object.values(SMART_CHIP_QUERY_CONSTANTS).forEach(({queryKeys}) => {
    emptyVariables[queryKeys.variablesKey] = [];
  });
  const variables = emptyVariables as BulkChipItemPrefetchQueryVariables;

  ids.forEach((id) => {
    const type = getSmartChipTypeFromId(id);
    const bucket = variables[SMART_CHIP_QUERY_CONSTANTS[type].queryKeys.variablesKey];
    bucket.push(id);
  });

  return variables;
}

// "globalChipState" is just a mechanism for globally triggering chip re-renders. It stores an empty object
// and assigns a new object to trigger an update (the new object will always shallowly differ from the
// previous)
const globalChipState = makeUseGlobalClientState<Object>(uuidv4());
const CHIP_STATE_ID = 'global-chip-status';

const chipQueue: Set<string> = new Set();
const chipPending: Set<string> = new Set();

let queryBatcherTimeout: NodeJS.Timeout | null;
let globalChipUpdateTimeout: NodeJS.Timeout | null;

/**
 * Marks a chip's id for data loading and prepares for batch loading.
 *
 * Uses setTimeout as a debouncing mechansim to collect chip ids in a render cycle. The
 * queryBatcherTimeout variable behaves something like a mutex.
 */
function enqueueChipId(chipId: string): void {
  chipQueue.add(chipId);
  if (queryBatcherTimeout == null) {
    queryBatcherTimeout = setTimeout(doBatchedQuery, 0);
  }
}

export function refreshChips(chipIds: Array<string>): void {
  for (const chipId of chipIds) {
    enqueueChipId(chipId);
  }
}

/**
 * Moves chips that are enqueued for batched load to the loading buffer and fires off a query. After the query
 * completes, it enqueues an update notification.
 */
async function doBatchedQuery(): Promise<void> {
  if (chipQueue.size === 0) {
    return;
  }

  queryBatcherTimeout = null;

  // Chips queries may be in flight at this point, so only query for chips which aren't already being loaded
  // in another query. The queryBatch variable is a temporary holding container for chipQueue while the
  // query is in flight (allowing chipQueue to fill up again as needed).
  const queryBatch: Set<string> = new Set();
  chipQueue.forEach((id) => {
    if (!chipPending.has(id)) {
      queryBatch.add(id);
    }
  });
  queryBatch.forEach((id) => chipPending.add(id));
  chipQueue.clear();

  const variables = getPrefetchQueryVariables(Array.from(queryBatch));
  await doQuerySafe<BulkChipItemPrefetchQuery, BulkChipItemPrefetchQueryVariables>(
    {query: BULK_CHIP_ITEM_PREFETCH_QUERY},
    {variables, fetchPolicy: 'network-only'}
  );

  queryBatch.forEach((id) => chipPending.delete(id));
  enqueueNotifyOtherChips();
}

/**
 * Flushes the loading buffer and sets up a global re-render notification to other chips.
 */
function enqueueNotifyOtherChips(): void {
  if (globalChipUpdateTimeout == null) {
    globalChipUpdateTimeout = setTimeout(notifyOtherChips, 0);
  }
}

/**
 * Queue re-render of all chips.
 */
export function debouncedGlobalChipRefresh(): void {
  enqueueNotifyOtherChips();
}

/**
 * Clears the debounce and notifies all chips that new data is available in the cache.
 */
function notifyOtherChips(): void {
  globalChipUpdateTimeout = null;
  globalChipState.updateState(CHIP_STATE_ID, uuidv4());
}

// Used in global testSetup.ts
export function flushSmartChipQueue(): void {
  chipQueue.clear();
  chipPending.clear();
  if (globalChipUpdateTimeout != null) {
    clearTimeout(globalChipUpdateTimeout);
    globalChipUpdateTimeout = null;
  }
  if (queryBatcherTimeout) {
    clearTimeout(queryBatcherTimeout);
    queryBatcherTimeout = null;
  }
}

interface SmartChipQueryResult<T extends SmartChipType> {
  handleClickLink: (e: BasicEvent) => void;
  isInaccessible: boolean | undefined;
  item: ChipItem<T> | undefined;
  itemUrl: string | null;
}

interface SmartChipQueryParams<T extends SmartChipType> {
  id?: string | null;
  onClick?: (e: BasicEvent, item: ChipItem<T>) => void;
  getUrlFn?: (item: ChipItem<T>) => string;
}

/**
 * Hook for a custom Chip component (i.e. RequestChip) to perform GraphQL data fetching.
 * @param - {
 *  getUrlFn - a function that extracts the URL to navigate to when the chip is clicked on from the ChipItem
 * }
 * @returns - {
 *  item: ChipItem - undefined if inaccessible or fetch hasn't completed
 *  isInaccessible: boolean
 *  handleClickLink: onClick handler
 *  itemUrl: onClick URL
 * }
 */
export default function useSmartChipQuery<T extends SmartChipType>({
  id,
  onClick,
  getUrlFn,
}: SmartChipQueryParams<T>): SmartChipQueryResult<T> {
  /**
   * An investigation into chip performance revealed that Apollo's cache write behavior is not particularly
   * performant for 100s to 1000s of chips at the same time.
   *
   * Prior to the batching implementation below, when chips completed loading they would each write the query
   * result to the cache before re-rendering the component with hydrated data. Due to the volume of writes,
   * and perhaps exacerbated by the fact that chips tend to over-fetch (resulting in numerous writes to
   * Apollo's normalized store), this caused the UI to be effectively locked for the duration of the cache
   * writing period. The greater the number of unique chips, the greater the number of concurrent writes, and
   * the worse the UX would be for the user.
   *
   * The implementation below uses useSmartChipQuery hook as a centralized orchestrator of batched chip data
   * fetching:
   *
   * - we maintain a queue of chip ids which acts as a buffer which populates each time a chip first renders.
   * - the buffer fills up and fires a query for chips in the queue using setTimeout
   * - when the query completes, an update signal is sent to Redux in the global client state domain
   * - the chips then proceed to read their result directly from the cache via readFragmentFromCache
   * - finally, queues are reset and ready to populate again (e.g. as prompted by new user activity)
   *
   * See this page for additional context:
   * https://benchling.atlassian.net/wiki/spaces/~5a3d7e44b66c2f374530cc6f/pages/828933303/Frontend+Chip+Perf+Investigation
   */
  const [_] = globalChipState.useGlobalClientState(CHIP_STATE_ID);

  if (id != null) {
    const data: ChipItemOrInaccessible<T> | null = readFragmentFromCache(getFragmentByChipId(id), id);

    if (data != null) {
      const isInaccessible = isInaccessibleGQL(data);
      const item: ChipItem<T> | undefined = isInaccessible ? undefined : data;

      return {
        item,
        isInaccessible,
        handleClickLink: (e: BasicEvent) => {
          if (item && onClick) {
            onClick(e, item);
          } else if (item && getUrlFn) {
            e.stopPropagation();
            const itemUrl = getUrlFn(item);
            navigateToURL(itemUrl ?? '');
          }
        },
        itemUrl: item && getUrlFn ? getUrlFn(item) : null,
      };
    } else if (!chipQueue.has(id) && !chipPending.has(id)) {
      enqueueChipId(id);
    }
  }

  return {
    item: undefined,
    isInaccessible: id == null ? true : undefined,
    handleClickLink: noop,
    itemUrl: null,
  };
}
